import React, { useState, useEffect } from "react";
import {
    Box,
    Grid,
    Typography,
    Button,
    Card,
    Divider,
    Chip,
    Stack,
} from "@mui/material";

import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import FlashOnOutlinedIcon from "@mui/icons-material/FlashOnOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { useLocation, useNavigate } from "react-router-dom";

import InnerImageZoom from "react-inner-image-zoom";
import "react-inner-image-zoom/lib/styles.min.css";

import CartToast from "../components/CartToast";

const ShopByBrandProductDetails = () => {
    const { state } = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const images = state?.image ? [state.image] : [];
    const [selectedImage, setSelectedImage] = useState(state?.image || "");

    // =========================
    // TOAST STATE
    // =========================
    const [toast, setToast] = useState({
        visible: false,
        message: "",
        type: "success",
    });

    const showToast = (message, type = "success") => {
        setToast({ visible: true, message, type });
        setTimeout(
            () => setToast((t) => ({ ...t, visible: false })),
            2800
        );
    };

    if (!state) {
        return (
            <Typography sx={{ textAlign: "center", mt: 6 }}>
                Product not found
            </Typography>
        );
    }

    // =========================
    // ADD TO CART
    // =========================
    const addToCart = () => {
        const cart = JSON.parse(localStorage.getItem("cart")) || [];
        const existing = cart.find((item) => item.title === state.title);
        const numericPrice = Number(
            (state.price || "0").replace(/[₹Rs.,\s]/g, "")
        );

        if (existing) {
            // already in cart — don't increment, just warn
            return true;
        }

        cart.push({
            title: state.title,
            image: selectedImage,
            price: numericPrice,
            qty: 1,
        });

        localStorage.setItem("cart", JSON.stringify(cart));
        window.dispatchEvent(new Event("cartUpdated"));
        return false;
    };

    return (
        <Box
            sx={{
                background:
                    "linear-gradient(to bottom, #f6f7f8, #eef1f3)",
                minHeight: "100vh",
                mt: { xs: "46px", md: "50px" },
                py: 5,
                px: 2,
            }}
        >
            <Box sx={{ maxWidth: 1180, mx: "auto" }}>

                {/* BACK BUTTON */}
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{
                        textTransform: "none",
                        borderRadius: "12px",
                        fontWeight: 600,
                        mb: 2.5,
                        color: "#1d2939",
                        borderColor: "#d0d5dd",
                        px: 2.2,
                        py: 0.8,
                        fontSize: "13px",
                        backgroundColor: "#fff",
                        "&:hover": {
                            borderColor: "#98a2b3",
                            backgroundColor: "#f8fafc",
                        },
                    }}
                >
                    Back
                </Button>

                {/* MAIN CARD */}
                <Card
                    sx={{
                        p: { xs: 2, md: 3 },
                        borderRadius: "28px",
                        background: "rgba(255,255,255,0.92)",
                        border: "1px solid rgba(226,232,240,0.9)",
                        boxShadow: "0 10px 35px rgba(15,23,42,0.06)",
                        backdropFilter: "blur(10px)",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            gap: 4,
                            alignItems: "flex-start",
                            flexDirection: { xs: "column", md: "row" },
                        }}
                    >
                        {/* LEFT IMAGE SECTION */}
                        <Box
                            sx={{
                                width: { xs: "100%", md: "42%" },
                                display: "flex",
                                gap: 1.5,
                            }}
                        >
                            {/* THUMBNAILS */}
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                }}
                            >
                                {images.map((img, index) => (
                                    <Box
                                        key={index}
                                        onMouseEnter={() =>
                                            setSelectedImage(img)
                                        }
                                        sx={{
                                            width: 62,
                                            height: 62,
                                            border:
                                                selectedImage === img
                                                    ? "2px solid #111827"
                                                    : "1px solid #d0d5dd",
                                            borderRadius: "14px",
                                            overflow: "hidden",
                                            cursor: "pointer",
                                            backgroundColor: "#fff",
                                            p: 0.5,
                                            transition: "0.3s ease",
                                            "&:hover": {
                                                borderColor: "#667085",
                                                transform: "translateY(-2px)",
                                            },
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={img}
                                            alt=""
                                            sx={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "contain",
                                            }}
                                        />
                                    </Box>
                                ))}
                            </Box>

                            {/* MAIN IMAGE */}
                            <Box
                                sx={{
                                    flex: 1,
                                    border: "1px solid #e4e7ec",
                                    borderRadius: "24px",
                                    background:
                                        "linear-gradient(145deg,#ffffff,#f8fafc)",
                                    p: 2,
                                    minHeight: 390,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    overflow: "hidden",
                                }}
                            >
                                <InnerImageZoom
                                    src={selectedImage}
                                    zoomSrc={selectedImage}
                                    zoomType="hover"
                                    zoomScale={1.5}
                                    hideHint={true}
                                    width={360}
                                    height={360}
                                    imgAttributes={{
                                        style: {
                                            objectFit: "contain",
                                            width: "100%",
                                            height: "100%",
                                        },
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* RIGHT SIDE */}
                        <Box sx={{ flex: 1 }}>
                            {/* TITLE */}
                            <Typography
                                variant="h4"
                                fontWeight={800}
                                sx={{
                                    color: "#111827",
                                    lineHeight: 1.25,
                                    fontSize: { xs: "28px", md: "36px" },
                                    wordBreak: "break-word",
                                }}
                            >
                                {state.title}
                            </Typography>

                            {/* BRAND + STOCK */}
                            <Stack
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                                sx={{ mt: 1.5, flexWrap: "wrap" }}
                            >
                                <Typography
                                    sx={{ color: "#667085", fontSize: "14px" }}
                                >
                                    Brand :
                                    <Box
                                        component="span"
                                        sx={{
                                            color: "#111827",
                                            fontWeight: 700,
                                            ml: 0.7,
                                        }}
                                    >
                                        {state.brand}
                                    </Box>
                                </Typography>

                                <Chip
                                    label={state.stock || "In stock"}
                                    size="small"
                                    sx={{
                                        backgroundColor: "#ecfdf3",
                                        color: "#027a48",
                                        fontWeight: 700,
                                        borderRadius: "8px",
                                    }}
                                />
                            </Stack>

                            {/* PRICE */}
                            <Stack
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                                sx={{ mt: 2.5, flexWrap: "wrap" }}
                            >
                                <Typography
                                    variant="h4"
                                    fontWeight={800}
                                    sx={{
                                        color: "#101828",
                                        fontSize: { xs: "32px", md: "40px" },
                                    }}
                                >
                                    {state.price}
                                </Typography>

                                {state.oldPrice && (
                                    <Typography
                                        sx={{
                                            textDecoration: "line-through",
                                            color: "#98a2b3",
                                            fontSize: "17px",
                                        }}
                                    >
                                        {state.oldPrice}
                                    </Typography>
                                )}

                                <Chip
                                    label="20% OFF"
                                    size="small"
                                    sx={{
                                        background: "#eff8ff",
                                        color: "#175cd3",
                                        fontWeight: 700,
                                    }}
                                />
                            </Stack>

                            {/* DESCRIPTION */}
                            <Typography
                                sx={{
                                    mt: 2.5,
                                    color: "#667085",
                                    lineHeight: 1.9,
                                    fontSize: "14px",
                                    maxWidth: "95%",
                                }}
                            >
                                Professional HVAC servicing equipment
                                engineered for high precision, durability,
                                and industrial performance. Ideal for
                                refrigeration and air-conditioning
                                maintenance applications.
                            </Typography>

                            {/* HIGHLIGHTS */}
                            <Box
                                sx={{
                                    mt: 3,
                                    background:
                                        "linear-gradient(135deg,#f8fafc,#f1f5f9)",
                                    border: "1px solid #e2e8f0",
                                    borderRadius: "18px",
                                    p: 2,
                                }}
                            >
                                <Typography
                                    fontWeight={700}
                                    sx={{
                                        color: "#111827",
                                        mb: 1.5,
                                        fontSize: "16px",
                                    }}
                                >
                                    Highlights
                                </Typography>

                                <Grid container spacing={1}>
                                    {[
                                        "Heavy-duty build",
                                        "High precision",
                                        "Portable design",
                                        "Energy efficient",
                                    ].map((item, index) => (
                                        <Grid item xs={6} key={index}>
                                            <Typography
                                                sx={{
                                                    fontSize: "13px",
                                                    color: "#475467",
                                                }}
                                            >
                                                • {item}
                                            </Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>

                            {/* BUTTONS */}
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                sx={{ mt: 4 }}
                            >
                                {/* ADD TO CART */}
                                <Button
                                    variant="contained"
                                    startIcon={<ShoppingCartOutlinedIcon />}
                                    sx={{
                                        background:
                                            "linear-gradient(135deg,#111827,#1f2937)",
                                        py: 1.2,
                                        px: 3,
                                        borderRadius: "14px",
                                        textTransform: "none",
                                        fontWeight: 700,
                                        fontSize: "14px",
                                        boxShadow:
                                            "0 10px 25px rgba(17,24,39,0.18)",
                                        "&:hover": {
                                            background:
                                                "linear-gradient(135deg,#0f172a,#111827)",
                                        },
                                    }}
                                    onClick={() => {
                                        const isDuplicate = addToCart();
                                        showToast(
                                            isDuplicate
                                                ? "Item already been added to cart!"
                                                : "Added to cart successfully",
                                            isDuplicate
                                                ? "warning"
                                                : "success"
                                        );
                                    }}
                                >
                                    Add to Cart
                                </Button>

                                {/* BUY NOW */}
                                <Button
                                    variant="contained"
                                    startIcon={<FlashOnOutlinedIcon />}
                                    sx={{
                                        background:
                                            "linear-gradient(135deg,#d4af37,#f5d76e)",
                                        py: 1.2,
                                        px: 3,
                                        borderRadius: "14px",
                                        textTransform: "none",
                                        fontWeight: 700,
                                        fontSize: "14px",
                                        color: "#111827",
                                        boxShadow:
                                            "0 10px 25px rgba(212,175,55,0.28)",
                                        "&:hover": {
                                            background:
                                                "linear-gradient(135deg,#c89c22,#e9c454)",
                                        },
                                    }}
                                    onClick={() => {
                                        addToCart();
                                        navigate("/checkout");
                                    }}
                                >
                                    Buy Now
                                </Button>
                            </Stack>
                        </Box>
                    </Box>

                    {/* LOWER SECTION */}
                    <Divider sx={{ my: 4, borderColor: "#eaecf0" }} />

                    <Grid container spacing={3}>
                        {/* DESCRIPTION */}
                        <Grid item xs={12} md={7}>
                            <Typography
                                variant="h6"
                                fontWeight={700}
                                sx={{ color: "#111827", mb: 1.5 }}
                            >
                                Product Description
                            </Typography>

                            <Typography
                                sx={{
                                    color: "#667085",
                                    lineHeight: 1.9,
                                    fontSize: "14px",
                                }}
                            >
                                {state.title} is manufactured using advanced
                                HVAC servicing technology to ensure superior
                                efficiency, precision, and reliability. This
                                equipment is ideal for professional
                                refrigeration and air-conditioning servicing
                                applications.
                            </Typography>
                        </Grid>

                        {/* SPECIFICATIONS */}
                        <Grid item xs={12} md={5}>
                            <Typography
                                variant="h6"
                                fontWeight={700}
                                sx={{ color: "#111827", mb: 1.5 }}
                            >
                                Specifications
                            </Typography>

                            <Grid container spacing={1.5}>
                                {[
                                    { label: "Brand", value: state.brand },
                                    {
                                        label: "Category",
                                        value: "HVAC Equipment",
                                    },
                                    {
                                        label: "Availability",
                                        value: state.stock || "In stock",
                                    },
                                    { label: "Warranty", value: "1 Year" },
                                ].map((item, index) => (
                                    <Grid item xs={6} key={index}>
                                        <Box
                                            sx={{
                                                background: "#f8fafc",
                                                border: "1px solid #e4e7ec",
                                                borderRadius: "14px",
                                                p: 1.5,
                                                height: "100%",
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    color: "#111827",
                                                    fontSize: "13px",
                                                    mb: 0.5,
                                                }}
                                            >
                                                {item.label}
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    color: "#667085",
                                                    fontSize: "13px",
                                                }}
                                            >
                                                {item.value}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </Grid>
                </Card>
            </Box>

            {/* CART TOAST */}
            <CartToast
                visible={toast.visible}
                message={toast.message}
                type={toast.type}
            />
        </Box>
    );
};

export default ShopByBrandProductDetails;